// ---------------------------------------------------------------------------
// SAVED SEARCHES / JOB ALERTS — single source of truth for the client side.
// ---------------------------------------------------------------------------
// A saved search is a contractor's standing filter set. The daily cron
// (edge function `job-alerts-digest`, cron `job-alerts-daily`, 14:00 UTC)
// re-runs each ACTIVE search against projects published since that search last
// sent, and emails a digest through `send-transactional-email`
// (template `job_alert_digest`, which carries a one-click unsubscribe link).
//
// Rules this module encodes so no component has to re-invent them:
//   * The filter shape mirrors the browse filters exactly (trade, state, city,
//     residential/commercial, minimum budget) — nothing is offered here that
//     the marketplace cannot actually filter on.
//   * min_budget compares against the STRUCTURED budget columns
//     (budget_max, falling back to budget_min). A project with NO budget on
//     file is still delivered — unknown is not "too small" — and the UI says
//     so out loud rather than hiding the behaviour.
//   * Radius is deliberately absent. Listings have no geocode yet, so an
//     "X miles" alert would be fabricated. City + state is the honest unit.
//   * Rows are never hard-deleted by the unsubscribe link; it only flips
//     `active` to false so the contractor can switch the alert back on.
// ---------------------------------------------------------------------------
import { supabase } from '@/lib/supabase';
import type { Lang } from '@/data/constants';

export interface SavedSearch {
  id: string;
  user_id: string;
  email: string;
  name: string;
  trades: string[];
  state: string | null;
  city: string | null;
  project_type: 'residential' | 'commercial' | null;
  min_budget: number | null;
  language: string;
  frequency: 'daily' | 'weekly';
  active: boolean;
  unsubscribe_token: string;
  last_sent_at: string | null;
  last_match_count: number;
  total_sent: number;
  created_at: string;
}

/** The editable part of a saved search (everything the form owns). */
export interface SavedSearchDraft {
  name: string;
  trades: string[];
  state: string | null;
  city: string | null;
  project_type: 'residential' | 'commercial' | null;
  min_budget: number | null;
  frequency: 'daily' | 'weekly';
}

export const EMPTY_DRAFT: SavedSearchDraft = {
  name: '',
  trades: [],
  state: null,
  city: null,
  project_type: null,
  min_budget: null,
  frequency: 'daily',
};

/** Hard ceiling so one account cannot turn the digest into a mail blast. */
export const MAX_SAVED_SEARCHES = 5;

/**
 * Minimum-budget steps, in whole dollars. Derived to line up with the floors of
 * BUDGET_BANDS in src/data/constants.ts — the same numbers stored in
 * mkt_listings.budget_min/max — so an alert threshold always corresponds to a
 * band a homeowner can actually pick.
 */
export const MIN_BUDGET_STEPS = [5000, 15000, 30000, 60000, 100000];

export const listSavedSearches = async (userId?: string | null): Promise<SavedSearch[]> => {
  if (!userId) return [];
  const { data } = await supabase
    .from('mkt_saved_searches')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  return (data || []) as SavedSearch[];
};

/** Validation shared by create and edit. Returns a bilingual message or null. */
export const validateDraft = (d: SavedSearchDraft, lang: Lang): string | null => {
  if (!d.name.trim()) {
    return lang === 'es' ? 'Ponle un nombre a la alerta.' : 'Give the alert a name.';
  }
  if (d.trades.length === 0 && !d.state && !d.city && !d.project_type && d.min_budget == null) {
    return lang === 'es'
      ? 'Elige al menos un filtro; una alerta sin filtros te enviaría todos los proyectos.'
      : 'Choose at least one filter — an alert with no filters would email you every project.';
  }
  if (d.min_budget != null && d.min_budget < 0) {
    return lang === 'es' ? 'El presupuesto mínimo no puede ser negativo.' : 'Minimum budget cannot be negative.';
  }
  return null;
};

export const createSavedSearch = async (
  userId: string,
  email: string,
  language: Lang,
  d: SavedSearchDraft
) => {
  const { error } = await supabase.from('mkt_saved_searches').insert({
    user_id: userId,
    email,
    language,
    name: d.name.trim(),
    trades: d.trades,
    state: d.state || null,
    city: d.city?.trim() || null,
    project_type: d.project_type,
    min_budget: d.min_budget,
    frequency: d.frequency,
  });
  return error;
};

export const updateSavedSearch = async (id: string, d: Partial<SavedSearchDraft> & { active?: boolean }) => {
  const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (d.name !== undefined) patch.name = d.name.trim();
  if (d.trades !== undefined) patch.trades = d.trades;
  if (d.state !== undefined) patch.state = d.state || null;
  if (d.city !== undefined) patch.city = d.city?.trim() || null;
  if (d.project_type !== undefined) patch.project_type = d.project_type;
  if (d.min_budget !== undefined) patch.min_budget = d.min_budget;
  if (d.frequency !== undefined) patch.frequency = d.frequency;
  if (d.active !== undefined) patch.active = d.active;
  const { error } = await supabase.from('mkt_saved_searches').update(patch).eq('id', id);
  return error;
};

export const deleteSavedSearch = async (id: string) => {
  const { error } = await supabase.from('mkt_saved_searches').delete().eq('id', id);
  return error;
};

/**
 * How many projects CURRENTLY on the marketplace this filter set would match.
 * Read-only preview run against the same public view and the same
 * `listing_open` gate the digest uses, so the number shown next to the form is
 * the real marketplace answer, not an estimate.
 */
export const previewMatchCount = async (d: SavedSearchDraft): Promise<number> => {
  let q = supabase
    .from('mkt_listings_public')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'published')
    .eq('listing_open', true);

  if (d.trades.length) q = q.in('trade', d.trades);
  if (d.state) q = q.eq('state', d.state);
  if (d.city) q = q.ilike('city', d.city.trim());
  if (d.project_type) q = q.eq('project_type', d.project_type);
  if (d.min_budget != null) {
    // Mirrors the digest: unknown budgets are included, never filtered out.
    q = q.or(`budget_max.gte.${d.min_budget},and(budget_max.is.null,budget_min.gte.${d.min_budget}),and(budget_max.is.null,budget_min.is.null)`);
  }
  const { count } = await q;
  return count || 0;
};

/** Human summary of a saved search's filters, e.g. "Roofing, HVAC · Fontana, CA · $15,000+". */
export const describeSearch = (
  s: Pick<SavedSearch, 'trades' | 'state' | 'city' | 'project_type' | 'min_budget'>,
  lang: Lang,
  tradeName: (slug: string) => string
): string => {
  const parts: string[] = [];
  if (s.trades?.length) parts.push(s.trades.map(tradeName).join(', '));
  const place = [s.city, s.state].filter(Boolean).join(', ');
  if (place) parts.push(place);
  if (s.project_type)
    parts.push(
      s.project_type === 'commercial'
        ? lang === 'es' ? 'Comercial' : 'Commercial'
        : lang === 'es' ? 'Residencial' : 'Residential'
    );
  if (s.min_budget != null) parts.push(`$${s.min_budget.toLocaleString('en-US')}+`);
  return parts.length
    ? parts.join(' · ')
    : lang === 'es' ? 'Todos los proyectos' : 'All projects';
};

// NOTE: the one-click unsubscribe URL is built SERVER-SIDE by the
// job-alerts-digest function, which is the only place that needs it. The front
// end never constructs that URL — it only consumes the token handed to it by
// the /alerts/unsubscribe page.
